// Pure rules for the Settings -> Domains forms. `@/…` imports are type-only so
// this module runs under `node --experimental-strip-types`.
import { friendlyApiError, type ErrorMessages } from './apiErrors.ts';
import type { CreateDomainRequest, DomainDetail, UpdateDomainRequest } from '@/types/domain';
import type { TLSCertificate, TLSMode } from '@/types/tlsSettings';

export type DomainFormMode = 'create' | 'edit';

export interface DomainFormValues {
  name: string;
  zone_id: string;
  /** Write-only; blank on edit keeps the stored token. */
  api_token: string;
  new_app_tls_mode: TLSMode;
  is_default: boolean;
}

export type DomainFormField = 'name' | 'zone_id' | 'api_token';
export type DomainFormErrors = Partial<Record<DomainFormField, string>>;

export const EMPTY_DOMAIN_FORM: DomainFormValues = {
  name: '',
  zone_id: '',
  api_token: '',
  new_app_tls_mode: 'letsencrypt',
  is_default: false,
};

// Same rule as the server (handlers/domains.go domainNameRe).
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
const ZONE_ID_RE = /^[a-f0-9]{32}$/;

/** Trim, lowercase, strip the trailing dot (as the server stores it). */
export const normalizeDomainName = (s: string): string => s.trim().replace(/\.$/, '').toLowerCase();

const normalizeZoneId = (s: string): string => s.trim().toLowerCase();

/**
 * Field messages; {} when the form can be sent. The name is fixed after creation; on edit an
 * unchanged zone ID (`currentZoneId`) is not re-validated.
 */
export function validateDomainForm(
  values: Pick<DomainFormValues, DomainFormField>,
  mode: DomainFormMode,
  currentZoneId?: string,
): DomainFormErrors {
  const errors: DomainFormErrors = {};
  if (mode === 'create') {
    const name = normalizeDomainName(values.name);
    if (!name) errors.name = 'Enter a domain name';
    else if (name.length > 253 || !DOMAIN_RE.test(name)) errors.name = 'Use a domain like apps.example.com';
  }
  const zone = normalizeZoneId(values.zone_id);
  const unchanged = mode === 'edit' && currentZoneId !== undefined && zone === normalizeZoneId(currentZoneId);
  if (!zone) errors.zone_id = 'Enter the Cloudflare zone ID';
  else if (!unchanged && !ZONE_ID_RE.test(zone)) errors.zone_id = 'A zone ID is 32 hexadecimal characters';
  if (mode === 'create' && !values.api_token.trim()) errors.api_token = 'Enter an API token';
  return errors;
}

export function createDomainRequest(form: DomainFormValues): CreateDomainRequest {
  return {
    name: normalizeDomainName(form.name),
    zone_id: normalizeZoneId(form.zone_id),
    api_token: form.api_token.trim(),
    new_app_tls_mode: form.new_app_tls_mode,
    is_default: form.is_default,
  };
}

export function domainFormFrom(d: DomainDetail): DomainFormValues {
  return { name: d.name, zone_id: d.zone_id, api_token: '', new_app_tls_mode: d.new_app_tls_mode, is_default: d.is_default };
}

/**
 * Only what changed: every sent zone/token/mode field makes the server re-check
 * Cloudflare. The default flag can only be set (the server refuses unsetting it).
 */
export function updateDomainRequest(d: DomainDetail, form: DomainFormValues): UpdateDomainRequest {
  const req: UpdateDomainRequest = {};
  const zone = normalizeZoneId(form.zone_id);
  if (zone !== d.zone_id) req.zone_id = zone;
  const token = form.api_token.trim();
  if (token) req.api_token = token;
  if (form.new_app_tls_mode !== d.new_app_tls_mode) req.new_app_tls_mode = form.new_app_tls_mode;
  if (form.is_default && !d.is_default) req.is_default = true;
  return req;
}

export const TLS_MODE_LABELS: Record<TLSMode, string> = {
  letsencrypt: "Let's Encrypt",
  origin: 'Origin certificate',
};

/** "Expires in 12 days"; null without a certificate. */
export function certExpiryLabel(cert: Pick<TLSCertificate, 'expires_in_days'> | null): string | null {
  if (!cert) return null;
  const days = cert.expires_in_days;
  // floor(hours / 24): 0 = less than a day left, negative = expired.
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires within 24 hours';
  return `Expires in ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** Traefik endpoints serving the domain's current certificate, of all Traefik endpoints. */
export function readyEndpoints(d: { endpoints: readonly { ready: boolean }[] }): { ready: number; total: number } {
  return { ready: d.endpoints.filter((e) => e.ready).length, total: d.endpoints.length };
}

const DOMAIN_ERRORS: ErrorMessages = {
  ZONE_MISMATCH: 'This zone ID belongs to a different domain. Copy the zone ID from this domain’s overview page in Cloudflare.',
  ZONE_UNREADABLE: 'The API token cannot read this zone. Give it Zone:Read and DNS:Edit permissions for the zone.',
  INVALID_ZONE_ID: 'A zone ID is 32 hexadecimal characters (Cloudflare → the domain’s overview page → API).',
  LETSENCRYPT_UNAVAILABLE:
    "Traefik's Cloudflare token (CLOUDFLARE_API_TOKEN) cannot issue Let's Encrypt certificates for this zone. Use the origin certificate mode instead.",
  DOMAIN_EXISTS: 'This domain has already been added.',
  DOMAIN_IS_DEFAULT: "The default domain can't be deleted. Make another domain the default first.",
  DOMAIN_IN_USE: (m) => `Can't delete the domain: ${m}.`,
  DOMAIN_CERT_IN_USE: (m) => `Can't remove the certificate: ${m}.`,
  DOMAIN_CHANGED: 'The domain was changed by someone else while saving. Reload and try again.',
  DOMAIN_NOT_FOUND: 'This domain no longer exists.',
  CLOUDFLARE_UNAVAILABLE: 'Cloudflare could not be reached. Try again in a moment.',
};

/** User-facing message for an error from api.domains. */
export function domainErrorMessage(err: unknown, fallback: string): string {
  return friendlyApiError(err, DOMAIN_ERRORS, fallback);
}
